import { Formik } from "formik";
import * as yup from "yup";
import toast from "react-hot-toast";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../../../components/ui/button";
import {
  FormikInput,
  FormikSelect,
  FormikSearchSelect,
  FormikDate,
  FormikSearchSectionSelect,
  FormikTime,
  FormikCheckbox,
} from "../../../components/formik-components";

import {
  IntCourtPriceMin,
  IntMax,
  IntMin,
  PaymentStatusEnum,
  PaymentTypeEnum,
  ResultStatus,
  ToCSharpFormat,
  DateminFormat,
  respEnum,
  SlotStatusEnum,
} from "../../../constants/appConstant";
import Common from "../../../helper/common";

import { useNavigate } from "react-router-dom";
import AddCustomer from "../../clubCustomers/components/addCustomer";
import animationData from "../../../assets/json/lotties/noData.json";
import common from "../../../helper/common";

const formValidations = yup.object({
  promoCode: yup.string().required("Promo Code is required"),
  value: yup
    .number()
    .required("Value is required")
    .min(0, "Value must be positive"),
  valueType: yup.number().required("Value Type is required"),
  startDate: yup.date().required("Start Date is required"),
  endDate: yup.date().required("End Date is required"),
  userUsageLimit: yup
    .number()
    .nullable()
    .test(
      "is-less-than-usageLimit",
      "User usage limit cannot be greater than usage limit",
      function (value) {
        const { usageLimit } = this.parent;
        if (value && usageLimit && value > usageLimit) {
          return false;
        }
        return true;
      }
    ),
});

const formInitialValue = {
  promoCode: "",
  value: "",
  valueType: "",
  startDate: "",
  endDate: "",
  minOrderAmount: "",
  maxDiscount: "",
  usageLimit: "",
  userUsageLimit: "",
  sportId: null,
  bookingFrom: "",
  bookingTill: "",
  promocodeDays: [],
  promocodeTimes: [],
  isFirstTimeOnly: false,
  isPlayerSpecific: false,
  isPublic: false,
  playerId: null,
};

export default function AddPromoCode({ onRequestClose, editData = null }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const formikRef = useRef();
  const [sports, setSports] = useState([]);
  const [players, setPlayers] = useState([]);
  const [timeRanges, setTimeRanges] = useState([]);

  const daysOfWeek = [
    { id: 0, name: "Sunday" },
    { id: 1, name: "Monday" },
    { id: 2, name: "Tuesday" },
    { id: 3, name: "Wednesday" },
    { id: 4, name: "Thursday" },
    { id: 5, name: "Friday" },
    { id: 6, name: "Saturday" },
  ];

  // Load sports data
  useEffect(() => {
    loadSports();
  }, []);

  // Load players data
  useEffect(() => {
    loadPlayers();
  }, []);

  // Populate form when editing
  useEffect(() => {
    if (editData && formikRef.current) {
      const formik = formikRef.current;

      // Set basic fields
      formik.setFieldValue("promoCode", editData.code || "");
      formik.setFieldValue("value", editData.discountValue || "");
      formik.setFieldValue("valueType", editData.discountType || "");
      formik.setFieldValue("startDate", editData.validFrom || "");
      formik.setFieldValue("endDate", editData.validTo || "");
      formik.setFieldValue("minOrderAmount", editData.minOrderAmount || "");
      formik.setFieldValue("maxDiscount", editData.maxDiscount || "");
      formik.setFieldValue("usageLimit", editData.usageLimit || "");
      formik.setFieldValue("userUsageLimit", editData.userUsageLimit || "");
      formik.setFieldValue("sportId", editData.sportId || null);
      formik.setFieldValue("bookingFrom", editData.bookingFrom || "");
      formik.setFieldValue("bookingTill", editData.bookingTill || "");
      formik.setFieldValue("promocodeDays", editData.promocodeDays || []);
      formik.setFieldValue(
        "isFirstTimeOnly",
        editData.isFirstTimeOnly || false
      );
      formik.setFieldValue(
        "isPlayerSpecific",
        editData.isPlayerSpecific || false
      );
      formik.setFieldValue("isPublic", editData.isPublic || false);
      formik.setFieldValue("playerId", editData.playerId || null);

      // Set time ranges
      if (editData.promocodeTimes && editData.promocodeTimes.length > 0) {
        setTimeRanges(
          editData.promocodeTimes.map((t) => ({
            timeFrom: t.timeFrom,
            timeTill: t.timeTill,
          }))
        );
      }
    }
  }, [editData]);

  const loadSports = async () => {
    try {
      const response = await Common.ApiService.getInstance().request(
        "GetSports",
        null,
        "GET"
      );

      if (response && response.status === respEnum.Success) {
        setSports(response.data || []);
      }
    } catch (error) {
      console.error("Failed to load sports:", error);
    }
  };

  const loadPlayers = async () => {
    try {
      const response = await Common.ApiService.getInstance().request(
        "GetUsers",
        null,
        "GET"
      );

      if (response && response.status === respEnum.Success) {
        setPlayers(response.data || []);
      }
    } catch (error) {
      console.error("Failed to load players:", error);
    }
  };

  const addTimeRange = () => {
    setTimeRanges([...timeRanges, { timeFrom: "", timeTill: "" }]);
  };

  const removeTimeRange = (index) => {
    setTimeRanges(timeRanges.filter((_, i) => i !== index));
  };

  const updateTimeRange = (index, field, value) => {
    const updated = [...timeRanges];
    updated[index][field] = value;
    setTimeRanges(updated);
  };

  const onSubmitData = async (d, action) => {
    try {
      let data = {
        ...d,
        promocodeTimes: timeRanges.filter((t) => t.timeFrom && t.timeTill),
      };

      // Map form data to API format
      const apiData = {
        id: editData?.id || null,
        code: data.promoCode,
        discountType: data.valueType,
        discountValue: data.value,
        minOrderAmount: data.minOrderAmount || null,
        maxDiscount: data.maxDiscount || null,
        usageLimit: data.usageLimit || null,
        userUsageLimit: data.userUsageLimit || null,
        validFrom: data.startDate,
        validTo: data.endDate,
        sportId: data.sportId,
        bookingFrom: data.bookingFrom || null,
        bookingTill: data.bookingTill || null,
        promocodeDays: data.promocodeDays,
        promocodeTimes: data.promocodeTimes,
        isFirstTimeOnly: data.isFirstTimeOnly,
        isPlayerSpecific: data.isPlayerSpecific,
        isPublic: data.isPublic,
        playerId: data.playerId || null,
      };

      const endpoint = editData ? "EditPromoCode" : "AddPromoCode";
      const response = await Common.ApiService.getInstance().request(
        endpoint,
        apiData,
        "POST"
      );

      if (response && response.status === respEnum.Success) {
        toast.success(
          `PromoCode ${editData ? "updated" : "created"} successfully!`
        );
        onRequestClose();
      } else {
        toast.error(response?.message || "Failed to save promocode");
      }
    } catch (error) {
      toast.error("Failed to save promocode");
      console.error("Error:", error);
    }
  };

  return (
    <Formik
      initialValues={formInitialValue}
      validationSchema={formValidations}
      innerRef={formikRef}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={onSubmitData}
    >
      {(formikProps) => (
        <div className="space-y-6">
          {/* Basic Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormikInput
                formikProps={formikProps}
                name={"promoCode"}
                placeholder={"Enter Promo Code"}
                label={"Promo Code"}
              />

              <div className="grid grid-cols-2 gap-2">
                <FormikInput
                  formikProps={formikProps}
                  name={"value"}
                  placeholder={"Enter Value"}
                  label={"Value"}
                  type={"number"}
                />
                <FormikSelect
                  formikProps={formikProps}
                  name={"valueType"}
                  placeholder={"Select Type"}
                  label={"Type"}
                  data={[
                    { id: 1, name: "Percentage (%)" },
                    { id: 2, name: "Fixed Amount" },
                  ]}
                />
              </div>
            </div>
          </div>

          {/* Validity Period */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Validity Period</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormikDate
                formikProps={formikProps}
                name={"startDate"}
                type={"datetime-local"}
                label={"Valid From"}
                min={Common.Utility.today}
              />
              <FormikDate
                formikProps={formikProps}
                name={"endDate"}
                type={"datetime-local"}
                label={"Valid Till"}
                min={Common.Utility.today}
              />
            </div>
          </div>

          {/* Restrictions */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Restrictions</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormikInput
                  formikProps={formikProps}
                  name={"minOrderAmount"}
                  placeholder={"Enter Minimum Order Amount"}
                  label={"Minimum Order Amount"}
                  type={"number"}
                />
                <FormikInput
                  formikProps={formikProps}
                  name={"maxDiscount"}
                  placeholder={"Enter Maximum Discount"}
                  label={"Maximum Discount"}
                  type={"number"}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormikInput
                  formikProps={formikProps}
                  name={"usageLimit"}
                  placeholder={"Enter Usage Limit"}
                  label={"Total Usage Limit"}
                  type={"number"}
                />
                <FormikInput
                  formikProps={formikProps}
                  name={"userUsageLimit"}
                  placeholder={"Enter User Usage Limit"}
                  label={"Per User Usage Limit"}
                  type={"number"}
                />
              </div>

              <FormikSearchSelect
                formikProps={formikProps}
                name={"sportId"}
                placeholder={"Select Sport (Optional)"}
                label={"Sport Restriction"}
                data={sports}
                isClearable={true}
              />

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formikProps.values.isPublic}
                  onChange={(e) =>
                    formikProps.setFieldValue("isPublic", e.target.checked)
                  }
                  className="rounded"
                />
                <label className="text-sm font-medium">Public Promocode</label>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-800 font-medium mb-1">
                  📢 Note:
                </p>
                <p className="text-xs text-blue-700">
                  If enabled, this promocode will be visible and available to
                  all customers.
                </p>
              </div>
            </div>
          </div>

          {/* Booking Time Restrictions */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">
              Booking Time Restrictions
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormikDate
                formikProps={formikProps}
                name={"bookingFrom"}
                type={"datetime-local"}
                label={"Booking From (Optional)"}
              />
              <FormikDate
                formikProps={formikProps}
                name={"bookingTill"}
                type={"datetime-local"}
                label={"Booking Till (Optional)"}
              />
            </div>
          </div>

          {/* Day Restrictions */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Day Restrictions</h3>
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Select Valid Days (Optional)
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {daysOfWeek.map((day) => (
                  <label key={day.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formikProps.values.promocodeDays.includes(
                        day.id
                      )}
                      onChange={(e) => {
                        const days = [...formikProps.values.promocodeDays];
                        if (e.target.checked) {
                          days.push(day.id);
                        } else {
                          const index = days.indexOf(day.id);
                          if (index > -1) days.splice(index, 1);
                        }
                        formikProps.setFieldValue("promocodeDays", days);
                      }}
                      className="rounded"
                    />
                    <span className="text-sm">{day.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Time Range Restrictions */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">
              Time Range Restrictions
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <label className="text-sm font-medium">
                  Valid Time Ranges (Optional)
                </label>
                <Button
                  type="button"
                  size="sm"
                  onClick={addTimeRange}
                  className="text-xs"
                >
                  Add Time Range
                </Button>
              </div>

              {timeRanges.map((range, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-2 p-3 bg-white rounded border"
                >
                  <input
                    type="time"
                    value={range.timeFrom}
                    onChange={(e) =>
                      updateTimeRange(index, "timeFrom", e.target.value)
                    }
                    className="px-2 py-1 border rounded"
                  />
                  <span>to</span>
                  <input
                    type="time"
                    value={range.timeTill}
                    onChange={(e) =>
                      updateTimeRange(index, "timeTill", e.target.value)
                    }
                    className="px-2 py-1 border rounded"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    onClick={() => removeTimeRange(index)}
                    className="text-xs"
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* Additional Options */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Additional Options</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formikProps.values.isFirstTimeOnly}
                  onChange={(e) =>
                    formikProps.setFieldValue(
                      "isFirstTimeOnly",
                      e.target.checked
                    )
                  }
                  className="rounded"
                />
                <label className="text-sm font-medium">
                  First Time Users Only
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formikProps.values.isPlayerSpecific}
                  onChange={(e) =>
                    formikProps.setFieldValue(
                      "isPlayerSpecific",
                      e.target.checked
                    )
                  }
                  className="rounded"
                />
                <label className="text-sm font-medium">Player Specific</label>
              </div>

              {formikProps.values.isPlayerSpecific && (
                <FormikSearchSelect
                  formikProps={formikProps}
                  name={"playerId"}
                  placeholder={"Select Player"}
                  label={"Select Player"}
                  data={players}
                />
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-3">
            <Button type="button" variant="outline" onClick={onRequestClose}>
              Cancel
            </Button>
            <Button
              onClick={(e) => {
                e.preventDefault();
                formikProps.handleSubmit();
              }}
              disabled={formikProps.isSubmitting}
              type="button"
            >
              {editData ? "Update PromoCode" : "Create PromoCode"}
            </Button>
          </div>
        </div>
      )}
    </Formik>
  );
}
